import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { Plus } from 'react-feather';
import { Helmet } from 'react-helmet';
import { mutate } from 'swr';
import { Col, Row, Tooltip } from 'antd';
import _ from 'lodash';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { getStoreList } from 'api/owners';
import { Button, Spin } from 'ui-kit';
import NewStore from 'components/Dashboard/Store/StoreModal/Actions/NewStore';
import EditStore from 'components/Dashboard/Store/StoreModal/Actions/EditStore';
import DuplicateStore from 'components/Dashboard/Store/StoreModal/Actions/DuplicateStore';
import OverviewLayout from '../OverviewLayout/OverviewLayout';
import StoreListItem from 'components/StoreList/StoreListItem';
import HospitableIntroModal from './HospitableIntroModal';
import OwnerRezIntroModal from './OwnerRezIntroModal';
import GuestyIntroModal from './GuestyIntroModal';
import HostawayIntroModal from './HostawayIntroModal';
import LodgifyIntroModal from './LodgifyIntroModal';
import { formatCurrency } from 'utils';
import { useSignedUser } from 'api/auth';

import './StoreList.scss';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

const StoreList = () => {
  const { data: user } = useSignedUser();
  const ownerId = user?.owner_id;

  const history = useHistory();
  const { search } = useLocation();
  const { data, isLoading, invalidate } = getStoreList(ownerId);
  const [openDropdownId, setOpenDropdownId] = useState(-1);
  const [isNewStoreModalOpen, setIsNewStoreModalOpen] = useState(false);
  const [isDuplicateStoreModalOpen, setIsDuplicateStoreModalOpen] = useState(false);
  const [isEditStoreModalOpen, setIsEditStoreModalOpen] = useState(false);
  const [isHospitableIntroModalOpen, setIsHospitableIntroModalOpen] = useState(false);
  const [isOwnerRezIntroModalOpen, setIsOwnerRezIntroModalOpen] = useState(false);
  const [isGuestyIntroModalOpen, setIsGuestyIntroModalOpen] = useState(false);
  const [isHostawayIntroModalOpen, setIsHostawayIntroModalOpen] = useState(false);
  const [isLodgifyIntroModalOpen, setIsLodgifyIntroModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState();

  const windowHandler = (ev) => {
    if (
      ['svg', 'circle', 'BUTTON'].includes(ev.target.tagName) ||
      ev.target.className === 'StoreListItem_moreDropdown dropdown'
    ) {
      return;
    }

    setOpenDropdownId(-1);
  };

  useEffect(() => {
    window.addEventListener('click', windowHandler);
    invalidate();
    return () => {
      window.removeEventListener('click', windowHandler);
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const introParam = searchParams.get('intro');

    if (introParam === 'ownerrez') {
      setIsOwnerRezIntroModalOpen(true);
    }
    if (introParam === 'hospitable') {
      setIsHospitableIntroModalOpen(true);
    }
    if (introParam === 'guesty' || introParam === 'guesty_lite') {
      setIsGuestyIntroModalOpen(true);
    }
    if (introParam === 'hostaway') {
      setIsHostawayIntroModalOpen(true);
    }
    if (introParam === 'lodgify') {
      setIsLodgifyIntroModalOpen(true);
    }
    searchParams.delete('intro');
    history.replace({
      search: searchParams.toString(),
    });
  }, [search]);

  useEffect(() => {
    const nextUrl = localStorage.getItem('nextUrl');
    if (nextUrl) {
      localStorage.removeItem('nextUrl');
      history.push({
        pathname: nextUrl,
      });
    }
  }, []);

  const sortCompare = (a, b) => {
    if (a.isVisible !== b.isVisible) {
      return a.isVisible ? -1 : 1;
    }
    if (a.storeName < b.storeName) {
      return -1;
    }
    if (a.storeName > b.storeName) {
      return 1;
    }
    return 0;
  };

  const filteredStores = useMemo(() => data?.stores?.sort(sortCompare), [data]);
  // We want to replicate the BE logic of owner_profit;
  // For negative payouts per store we are going to create Balance Adjustments, not include them into Payouts;
  // TODO: Move this logic to BE
  const sumOfStoresPayouts = useMemo(
    () =>
      _.sumBy(filteredStores, (store) => {
        if (store.totalPayoutAmountCurrentMonth < 0) {
          return 0;
        }
        return store.totalPayoutAmountCurrentMonth;
      }),
    [filteredStores],
  );

  const handleNewStoreModalOpen = () => {
    setIsNewStoreModalOpen(true);
  };

  const handleNewStoreModalClose = () => {
    setIsNewStoreModalOpen(false);
  };

  const handleDuplicateStoreModalOpen = (store) => {
    setSelectedStore(store);
    setIsDuplicateStoreModalOpen(true);
  };

  const handleDuplicateStoreModalClose = () => {
    setIsDuplicateStoreModalOpen(false);
    setSelectedStore(null);
  };

  const handleEditStoreModalOpen = (store) => {
    setSelectedStore(store);
    setIsEditStoreModalOpen(true);
  };

  const handleEditStoreModalClose = () => {
    setIsEditStoreModalOpen(false);
    setSelectedStore(null);
  };

  if (data && !isLoading && data?.stores?.length === 0) {
    return (
      <Redirect
        to={{ pathname: URLS.ONBOARDING_VIDEO, state: { stores: data?.stores?.length, ownerId } }}
      />
    );
  }

  return (
    <OverviewLayout>
      <Helmet>
        <title>Store List | The Host Co</title>
      </Helmet>

      <div className="StoreList">
        <div className="StoreList_header">
          <div className="StoreList_headerHeader">
            <div className="StoreList_title">Stores</div>
            <div className="StoreList_activities">
              <Button
                className="StoreList_addProductButton small"
                variant="contained"
                onClick={handleNewStoreModalOpen}
                startIcon={<Plus />}
              >
                Add store
              </Button>
            </div>
          </div>
          {data && !isLoading && data?.stores?.length === 0 && (
            <div className="message">
              <span className="message heading">No stores found</span>
              <span className="message text">
                You don’t currently have any stores. Please add a store if you’d like to sell to
                your guests.
              </span>
            </div>
          )}
          <Button
            fullWidth
            variant="outlined"
            color="textDark"
            onClick={() => history.push(reverse({ url: URLS.ORDERS }))}
          >
            See recent orders
          </Button>
        </div>
        <Spin spinning={isLoading}>
          <div className="StoreList_body">
            {data &&
              !isLoading &&
              data?.stores?.length > 0 &&
              (filteredStores && filteredStores.length > 0 ? (
                <div className="StoreList_table">
                  <div className="StoreList_row header_mobile">
                    Total sales this month: {formatCurrency(sumOfStoresPayouts, data.currency)}
                    <p className="note">/Total may not be exact due to process timing/</p>
                  </div>

                  <Row className="StoreList_row header">
                    <Col className="StoreList_col label image" span={2}>
                      {' '}
                    </Col>
                    <Col className="StoreList_col label name" span={8}>
                      Store Name
                    </Col>
                    <Col className="StoreList_col label share" span={2}>
                      Store Link
                    </Col>
                    <Col className="StoreList_col label zipCode" span={2}>
                      Zip Code
                    </Col>
                    <Col className="StoreList_col label products" span={2}>
                      Products
                    </Col>
                    <Col className="StoreList_col label orders" span={2}>
                      Orders
                      <p>(all time)</p>
                    </Col>
                    <Col className="StoreList_col label sales" span={3}>
                      <p>Payout this month</p>
                      <p>
                        (Total: {formatCurrency(sumOfStoresPayouts, data.currency)}){' '}
                        <Tooltip title="Total may not be exact due to process timing">
                          <ExclamationCircleOutlined />
                        </Tooltip>
                      </p>
                    </Col>
                    <Col className="StoreList_col label more" span={1}>
                      {' '}
                    </Col>
                  </Row>
                  {filteredStores.map((store) => {
                    return (
                      <StoreListItem
                        classname="StoreList_item"
                        key={store.id}
                        store={store}
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        openDuplicateModal={handleDuplicateStoreModalOpen}
                        openEditModal={handleEditStoreModalOpen}
                        currency={data?.currency}
                        onUpdate={invalidate}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="message">
                  <span className="message heading">No Stores Found</span>
                  <span className="message text">
                    We couldn&apos;t find any stores that matched your key words. Please try using a
                    different search term.
                  </span>
                </div>
              ))}
          </div>
        </Spin>
      </div>
      <OwnerRezIntroModal
        isOpen={isOwnerRezIntroModalOpen}
        onCancel={() => setIsOwnerRezIntroModalOpen(false)}
      />
      <HospitableIntroModal
        isOpen={isHospitableIntroModalOpen}
        onCancel={() => setIsHospitableIntroModalOpen(false)}
      />

      <GuestyIntroModal
        isOpen={isGuestyIntroModalOpen}
        onCancel={() => setIsGuestyIntroModalOpen(false)}
      />

      <HostawayIntroModal
        isOpen={isHostawayIntroModalOpen}
        onCancel={() => setIsHostawayIntroModalOpen(false)}
      />

      <LodgifyIntroModal
        isOpen={isLodgifyIntroModalOpen}
        onCancel={() => setIsLodgifyIntroModalOpen(false)}
      />

      <NewStore
        isOpen={isNewStoreModalOpen}
        onCancel={handleNewStoreModalClose}
        onSuccess={(store) =>
          history.push(
            reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            }),
          )
        }
      />
      <DuplicateStore
        isOpen={isDuplicateStoreModalOpen}
        store={selectedStore}
        onCancel={handleDuplicateStoreModalClose}
        onSuccess={(store) =>
          history.push(
            reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            }),
          )
        }
      />
      <EditStore
        isOpen={isEditStoreModalOpen}
        store={selectedStore}
        onCancel={handleEditStoreModalClose}
        onSuccess={() => {
          mutate(`/services/owner/${ownerId}/stores/`);
          handleEditStoreModalClose();
        }}
      />
    </OverviewLayout>
  );
};

export default StoreList;
